'use client'

import React, { useCallback, useEffect, useRef, useState, KeyboardEvent, Dispatch } from 'react'
import debounce from 'lodash/debounce'
import type { DebouncedFunc } from 'lodash'
import classNames from 'classnames'
import { useRouter } from 'next/navigation'
// Hooks
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'

// Components
import { getMainCategoryIcon } from '@dustin-web/microsite-components/src/utils/main-category-icons'
import { stripTrailingSlash } from '@dustin-web/microsite-components/src/utils/urls'
import Accordion from '@dustin-web/microsite-components/src/accordion'
import { TextSkeleton } from '@dustin-web/microsite-components/src/skeleton'

// Types
import {
  type DesktopState,
  type DesktopStateAction,
  DesktopStateActionType,
  MobileStateActionType,
  useHeaderContext,
} from '@dustin-web/microsite-baselayout/src/components/header/header-context'

import { useCategoryNavigation } from '@dustin-web/product-components/src/hooks/dataloader/use-category-navigation'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import {
  NavigationEventType,
  NavigationSelectionType,
} from '@dustin-web/microsite-components/src/utils/digital-data-types'
import Icon from '@dustin-web/microsite-components/src/icon'

export const getGaTitle = (id: string) => {
  return {
    '2037749': 'computer-and-tablets',
    '2037751': 'computer-accessories',
    '2029798': 'server-and-storage',
    '2029786': 'network',
    '2029772': 'computer-components',
    '2029808': 'client-storage',
    '2037759': 'printers-and-supplies',
    '2029810': 'phones-and-gps',
    '2037866': 'audio-and-video',
    '2029978': 'point-of-sale',
    '2029788': 'office-equipment',
    '2037737': 'software',
    '2029898': 'bargain',
  }[id]
}

type Props = {
  showMobileCategories?: boolean
  shallowData: any
}
const TOP_CATEGORY_ID = '2037738'
const BARGAIN_CATEGORY_ID = '2029898'

const CategoryTreeMenu = ({ showMobileCategories = false, shallowData }: Props) => {
  const { dispatchMobile, dispatchDesktop, mobile, desktop } = useHeaderContext()
  const { isSmallScreen } = useResponsive()
  const { translate } = useTranslations()
  const isActive = desktop.activeMegaMenu === 'category' || showMobileCategories
  const { data, isValidating: loading } = useCategoryNavigation(isActive)

  const [activeCategory, setActiveCategory] = useState(TOP_CATEGORY_ID)
  const [currentId, setCurrentStateId] = useState('')
  const [focusOnSubCategories, setFocusOnSubCategories] = useState(false)

  const mainCategories = shallowData?.categoryNavigation?.children || []
  const allCategories = data?.categoryNavigation?.children

  const rootCategoryId = mainCategories[0]?.categoryId

  // debounce to improve interaction when moving between menu options
  const setCurrentId = debounce(id => setCurrentStateId(id), 100)
  const isCurrentId = (id: string) => id === currentId

  useEffect(() => {
    if (!loading && rootCategoryId) {
      setCurrentStateId(rootCategoryId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const onCategoryClick = useCallback(
    (id: string, name: string) => {
      if (mobile.breadcrumbs) {
        setActiveCategory(id)

        dispatchMobile({
          type: MobileStateActionType.SET_BREADCRUMBS,
          payload: {
            breadcrumbs: [
              ...mobile.breadcrumbs,
              {
                title: name,
                categoryId: id,
                action: () => setActiveCategory(TOP_CATEGORY_ID),
              },
            ],
          },
        })
      }
    },
    [mobile.breadcrumbs]
  )

  if (isSmallScreen) {
    return (
      <Mobile
        showCampaigns
        allCategories={allCategories}
        showMobileCategories={showMobileCategories}
        activeCategory={activeCategory}
        translate={translate}
        onCategoryClick={onCategoryClick}
      />
    )
  } else {
    return (
      <Desktop
        showCampaigns
        mainCategories={mainCategories}
        allCategories={allCategories}
        setCurrentId={setCurrentId}
        focusOnSubCategories={focusOnSubCategories}
        loading={loading}
        showSkeleton={loading || !allCategories?.length}
        data={data}
        desktop={desktop}
        isCurrentId={isCurrentId}
        translate={translate}
        setFocusOnSubCategories={setFocusOnSubCategories}
        dispatchDesktop={dispatchDesktop}
      />
    )
  }
}

type CommonProps = {
  allCategories: any
  showCampaigns?: boolean
  translate: (param: string, obj?: any) => string
}
type MobileProps = {
  showMobileCategories: boolean
  activeCategory: string
  onCategoryClick: (icon: any, text: any) => void
}
type DesktopProps = {
  mainCategories: any
  setCurrentId: DebouncedFunc<any>
  loading: boolean
  data: any
  showSkeleton: boolean
  desktop: DesktopState
  focusOnSubCategories: boolean
  isCurrentId: (id: string) => boolean
  setFocusOnSubCategories: (state: boolean) => void
  dispatchDesktop: Dispatch<DesktopStateAction>
}

const Desktop = ({
  mainCategories,
  allCategories,
  showCampaigns,
  setCurrentId,
  focusOnSubCategories,
  data,
  desktop,
  showSkeleton,
  isCurrentId,
  translate,
  setFocusOnSubCategories,
  dispatchDesktop,
}: CommonProps & DesktopProps) => {
  const categoryListRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (desktop.activeMegaMenu === desktop.focusMegaMenu) {
      categoryListRef.current?.focus()

      dispatchDesktop({
        type: DesktopStateActionType.SET_FOCUS_MEGA_MENU,
        payload: {
          focusMegaMenu: '',
        },
      })
    }
  }, [desktop.activeMegaMenu, desktop.focusMegaMenu])

  return (
    <div className="row no-gutters u-h-100 a-fade-in">
      <div
        tabIndex={0}
        ref={categoryListRef}
        className="c-header__category-list-wrapper col-3 u-pt-24">
        <ul
          className="c-header__category-list ul-stripped"
          onMouseLeave={() => setCurrentId.cancel()}
          role="tablist">
          <CategoryListLink
            key="bargains"
            icon="bargain"
            onActive={() => setCurrentId(BARGAIN_CATEGORY_ID)}
            linkHref={stripTrailingSlash(translate('Bargains_URL'))}
            gaTitle={getGaTitle(BARGAIN_CATEGORY_ID)}
            text={translate('Show_Bargains')}
            isCurrent={isCurrentId(BARGAIN_CATEGORY_ID)}
            subCategories={false}
            onClick={() => {
              DigitalData.pushNavigationInteraction(
                NavigationEventType.MainMenu,
                'bargains',
                'link_click'
              )
            }}
          />

          {showCampaigns && (
            <CategoryListLink
              key="show_campaigns"
              icon="label"
              gaTitle="campaigns"
              onActive={() => setCurrentId('0000000')}
              linkHref={stripTrailingSlash(translate('Campaigns_URL'))}
              text={translate('Show_Campaigns')}
              isCurrent={false}
              subCategories={false}
              onClick={() => {
                DigitalData.pushNavigationInteraction(
                  NavigationEventType.MainMenu,
                  'campaigns',
                  'link_click'
                )
              }}
            />
          )}

          <CategoryListLink
            key="news"
            gaTitle="news"
            icon="idea"
            onActive={() => setCurrentId('0000001')}
            linkHref={translate('New_Products_URL')}
            text={translate('Header_News')}
            isCurrent={false}
            subCategories={false}
            onClick={() => {
              DigitalData.pushNavigationInteraction(
                NavigationEventType.MainMenu,
                'news',
                'link_click'
              )
            }}
          />

          <li className="c-mobile-header-menu__primary-list-devider-desk"></li>

          {mainCategories.map((child: any, key: number) => (
            <React.Fragment key={child.categoryId + key}>
              <CategoryListLink
                onActive={() => setCurrentId(child.categoryId)}
                linkHref={stripTrailingSlash(child.url || '')}
                gaTitle={getGaTitle(child.categoryId)}
                icon={child.categoryId}
                text={child.categoryDisplayName}
                isCurrent={isCurrentId(child.categoryId)}
                setFocusOnSubCategories={setFocusOnSubCategories}
                onClick={() => {
                  DigitalData.pushNavigationInteraction(
                    NavigationEventType.MainMenu,
                    child.categoryEnglishDisplayName,
                    'link_click',
                    { selectionType: NavigationSelectionType.Category }
                  )
                }}
              />

              {key === mainCategories.length - 1 && (
                <li className="u-px-16 u-py-16" key={'all_categories'}>
                  <a
                    data-ga-title="show-all-categories"
                    href={stripTrailingSlash(translate('AllCategories_URL'))}>
                    {translate('Show_all_categories')}
                  </a>
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </div>

      <div className="c-header__category-content-wrapper col-9 u-pt-24 u-pl-32 u-pr-8">
        {showSkeleton && <SkeletonSubCategoryColumns />}

        {allCategories?.map(
          (item: any, key: number) =>
            isCurrentId(item.categoryId) && (
              <CategorySubCategoryColumns
                key={item.categoryId + key}
                data={data}
                focusOnSubCategories={focusOnSubCategories}
                categoryItem={item}
                isCurrentId={isCurrentId}
              />
            )
        )}
      </div>
    </div>
  )
}

const Mobile = ({
  allCategories,
  showMobileCategories,
  activeCategory,
  translate,
  showCampaigns,
  onCategoryClick,
}: CommonProps & MobileProps) => {
  const router = useRouter()
  return (
    <>
      {/* First-level categories */}
      <ul
        className={classNames('c-mobile-header-menu__primary-list ul-stripped', {
          'c-mobile-header-menu__primary-list--upcoming': !showMobileCategories,
          'c-mobile-header-menu__primary-list--dismissed': activeCategory !== TOP_CATEGORY_ID,
        })}>
        <li className="u-fw-bold">
          <a
            href={stripTrailingSlash(translate('AllCategories_URL'))}
            className="c-mobile-header-menu__primary-action"
            key="all_categories">
            {translate('Show_all_categories')}
          </a>
        </li>

        <CategoryListButton
          icon={'bargain'}
          text={translate('Show_Bargains')}
          gaTitle={translate('Show_Bargains')}
          onClick={() => router.push(stripTrailingSlash(translate('Bargains_URL')))}
          dataTestIdOverride={'category-bargains'}
        />

        {showCampaigns && (
          <CategoryListButton
            icon={'label'}
            text={translate('Show_Campaigns')}
            gaTitle={translate('Show_Campaigns')}
            onClick={() => router.push(stripTrailingSlash(translate('Campaigns_URL')))}
            dataTestIdOverride={'category-campaigns'}
          />
        )}

        {allCategories &&
          allCategories.map((child: any, key: number) => (
            <CategoryListButton
              key={child.categoryId + key}
              icon={child.categoryId}
              text={child.categoryDisplayName}
              gaTitle={child.categoryDisplayName}
              onClick={() => onCategoryClick(child.categoryId, child.categoryDisplayName ?? '')}
            />
          ))}
      </ul>

      {/* Second-level categories */}
      {allCategories &&
        allCategories?.map((child: any, key: number) => (
          <ul
            key={child.categoryId + key}
            className={classNames('c-mobile-header-menu__primary-list ul-stripped ', {
              'c-mobile-header-menu__primary-list--upcoming': activeCategory !== child.categoryId,
            })}>
            <li className="u-fw-bold">
              <a
                href={stripTrailingSlash(child.url ?? '')}
                key="all_categories_2nd"
                className="c-mobile-header-menu__primary-action"
                style={{ height: 'auto', marginTop: '5px' }}>
                {translate('Header_ShowAllCategory', {
                  category: child.categoryDisplayName?.toLowerCase(),
                })}
              </a>
            </li>
            {child.children?.map((grandchild: any, key: number) => (
              <li key={grandchild.categoryDisplayName + key}>
                {grandchild.children?.length ? (
                  <Accordion
                    title={
                      <span
                        data-test-id="category-lvl-2"
                        className="u-overflow-ellipsis"
                        style={{ maxWidth: '80%' }}>
                        {grandchild.categoryDisplayName}
                      </span>
                    }>
                    <>
                      {/* Third-level categories */}
                      <ul className="ul-stripped">
                        <li>
                          <a
                            className="a-stripped u-fw-bold"
                            key="all_categories_3rd"
                            href={stripTrailingSlash(grandchild.url ?? '')}>
                            {translate('Header_ShowAllCategory', {
                              category: grandchild.categoryDisplayName?.toLowerCase(),
                            })}
                          </a>
                        </li>
                        {grandchild.children?.map((grandgrandchild: any, key: number) => (
                          <li key={grandgrandchild.categoryDisplayName + key}>
                            <a
                              className="a-stripped"
                              data-test-id="category-lvl-3"
                              href={stripTrailingSlash(grandgrandchild.url ?? '')}>
                              {grandgrandchild.categoryDisplayName}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </>
                  </Accordion>
                ) : (
                  <a
                    className="c-mobile-header-menu__primary-action"
                    href={stripTrailingSlash(grandchild.url ?? '')}>
                    {grandchild.categoryDisplayName}
                  </a>
                )}
              </li>
            ))}
          </ul>
        ))}
    </>
  )
}
type CommonLinkProps = {
  icon: string
  gaTitle: string | undefined
  text: string
}

type CategoryListButtonProps = {
  onClick: () => void
  dataTestIdOverride?: string
}

const CategoryListButton = ({
  icon,
  gaTitle,
  onClick,
  text,
  dataTestIdOverride,
}: CommonLinkProps & CategoryListButtonProps) => (
  <li className="d-flex align-items-center">
    {getMainCategoryIcon(icon, 'i-16')}
    <button
      data-ga-title={gaTitle}
      data-test-id={dataTestIdOverride || 'category-lvl-1'}
      className="c-mobile-header-menu__primary-action c-mobile-header-menu__primary-action--w-sub-menu u-pl-16 u-mb-0"
      onClick={onClick}>
      {text}
    </button>
  </li>
)

type CategoryListLink = {
  onActive: () => void
  setFocusOnSubCategories?: (state: boolean) => void
  linkHref: string
  isCurrent: boolean
  onClick: () => void
  subCategories?: boolean
}

const CategoryListLink = ({
  onActive,
  setFocusOnSubCategories,
  linkHref,
  text,
  icon,
  gaTitle,
  isCurrent,
  subCategories = true,
  onClick,
}: CommonLinkProps & CategoryListLink) => {
  const { translate } = useTranslations()

  const handleEnterPress = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key !== 'Enter' || !subCategories) {
      return
    }

    event.preventDefault()
    onActive()

    if (setFocusOnSubCategories) {
      setFocusOnSubCategories(true)
      setTimeout(() => setFocusOnSubCategories(false), 200)
    }
  }

  return (
    <li onMouseEnter={onActive} className="d-flex align-items-center">
      <a
        onClick={onClick}
        className={classNames('c-header__lvl1-category-link a-stripped', {
          'c-header__lvl1-category-link--active': isCurrent,
        })}
        data-ga-title={gaTitle}
        href={linkHref}>
        <span className="d-flex align-items-center">
          {getMainCategoryIcon(icon, 'i-20 u-mr-8 flex-shrink-0')}
          <span className="u-fs-16">{text}</span>
        </span>
      </a>
      {subCategories && (
        <a
          className="c-header__lvl1-category-link-subcategories"
          aria-label={translate('Header_Sub_Category_Accessibility')}
          onKeyDown={handleEnterPress}
          href="#">
          <Icon name="arrow-right" className="i-20" />
        </a>
      )}
    </li>
  )
}

type CategorySubCategoryColumns = {
  data: any
  categoryItem: any
  focusOnSubCategories?: boolean
  isCurrentId: (arg: string) => boolean
}

const CategorySubCategoryColumns = ({
  data,
  categoryItem,
  focusOnSubCategories,
  isCurrentId,
}: CategorySubCategoryColumns) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (focusOnSubCategories) {
      containerRef.current?.focus()
    }
  }, [focusOnSubCategories])

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className={classNames('row sm-gutters', {
        'd-none': !isCurrentId(categoryItem.categoryId),
      })}
      role="tabpanel"
      id={`tab-pane-${categoryItem.categoryId}`}
      key={categoryItem.categoryId}
      aria-labelledby={'TODO: categoryItemparentCurrentId (?)'}>
      {data &&
        categoryItem.children?.map((child: any, key: number) => (
          <div key={child.categoryDisplayName + key} className="col-4 col-xl-3 u-mb-16">
            <a
              href={stripTrailingSlash(child.url ?? '')}
              className="c-header__lvl2-category-link a-stripped h4">
              {child.categoryDisplayName}
            </a>
            <ul className="ul-stripped u-fs-14">
              {child.children?.map((grandChild: any, key: number) => (
                <li key={grandChild.categoryDisplayName + key}>
                  <a
                    data-test-id="category-lvl-3"
                    className="c-header__lvl3-category-link a-stripped"
                    href={stripTrailingSlash(grandChild.url ?? '')}>
                    {grandChild.categoryDisplayName}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  )
}

const SkeletonSubCategoryColumns = () => (
  <div className="row sm-gutters">
    {[...Array(9)].map((_, i) => (
      <div className="col-4 col-xl-3 u-mb-16" key={i}>
        <TextSkeleton type="h3" shade="light-on-dark-bg" />
        <TextSkeleton type="p" shade="light-on-dark-bg" />
      </div>
    ))}
  </div>
)

export default CategoryTreeMenu
